import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import styled from "styled-components";
import { ConversationContext } from "./context";
import { ConversationPaneProviders } from "./conversation-pane-providers";
import { ConversationPaneHeader } from "./conversation-pane-header";
import { ReplyApp } from "./reply/app";
import { CenteredSpinner } from "@coxauto-ui/core/components";
import { ConversationTypeEnum } from "@coxauto-ui/cxm/communications/interfaces";
import ConversationPaneTabs from "./conversation-pane-tabs";
import {
  contactDetailsError as contactDetailsErrorTestId,
  conversationListError,
  conversationListTestId
} from "./testIds";
import { MessageItemDetails } from "./list/item-details";
import ConversationPaneContent from "./conversation-pane-content";
import OpenReplyButton from "./reply/open-reply-button";
import { XMarkIcon } from "@interstate/components/Icons";
import { Button } from "@interstate/components/Button";
import { Alert } from "@interstate/components/Alert";
import { GenerativeAI } from "@coxauto-ui/cxm/communications/generative-ai";
import { ErrorAlert } from "./components/error-alert";
import {
  deriveDisplayTextForReplyHeader,
  isTextVisuallyEmpty
} from "@coxauto-ui/cxm/communications/util";
import {
  StationeryOptions,
  getStationery
} from "@coxauto-ui/cxm/communications/api";
import { useCxmFlags } from "@coxauto-ui/core/third-party/launch-darkly";
import { useConversationDetailsState } from "./hooks/useConversationDetailsState";
import { useEmailReplyState } from "./hooks/useEmailReplyState";
import { useTextReplyState } from "./hooks/useTextReplyState";
import { usePhoneReplyState } from "./hooks/usePhoneReplyState";

const StyledConversationPane = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0px;
  right: 0px;
  bottom: 0px;
  left: 0px;
  background-color: #fff;
  overflow-x: hidden;

  .cx-tabs__content {
    display: none;
  }

  .conversation-icon.gray {
    color: #959595;
  }

  .conversation-icon.red {
    color: #d41e27;
  }

  .conversation-icon.green {
    color: #479230;
  }

  .conversation-icon.blue {
    color: #2372b8;
  }

  .conversation-icon.orange {
    color: #f87216;
  }

  .item {
    .message-item {
      padding-top: 0.75em;
      padding-bottom: 0.75em;
      word-wrap: break-word;
    }

    .message-item-bubble {
      border-radius: 0.3em;
      cursor: pointer;
      padding: 0.5em;
      display: inline-block;
      max-width: 85%;
      text-align: left;
      min-width: min-content;
      overflow-wrap: anywhere;
    }

    .message-item-bubble.full {
      width: 85%;
    }
    .message-item-inbound {
      text-align: left;
    }

    .message-item-inbound-bubble {
      background-color: #fff;
      color: #000;
      margin-bottom: 4px;
    }

    .message-item-attachment-inbound-bubble {
      padding: 0em;
      margin-bottom: 4px;
    }

    .message-item-attachment-inbound-view {
      margin-bottom: 10px;
      display: flex;
      justify-content: flex-start;
    }

    .message-item-outbound {
      text-align: right;
    }

    .message-item-outbound-bubble {
      background-color: #cce8ff;
      color: #000;
      margin-bottom: 4px;
    }

    .message-item-outbound-bubble-error {
      background-color: #e3e3e3;
      color: #5a5a5a;
    }

    .message-item-attachment-outbound-bubble {
      padding: 0em;
      margin-bottom: 4px;
    }

    .message-item-attachment-outbound-view {
      margin-top: 5px;
      margin-bottom: 10px;
      display: flex;
      justify-content: flex-end;
    }

    .attachment-view-container {
      border-radius: 8px;
      width: 150px;
      height: 150px;
      background-color: #e3e3e3;
      box-shadow: 0 0 4px 0 #b2b2b2;
      cursor: pointer;
    }

    .attachment-view-thumbnail-container {
      width: 100%;
      height: 100%;
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #959595;

      .attachment-view-thumbnail {
        border-radius: 8px;
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      .fallback-container {
        text-align: center;
      }

      .photo-icon {
        font-size: xx-large;
      }

      .error-message {
        margin-top: 0;
        font-size: 10px;
      }

      .attachment-view-thumbnail-download-container {
        position: absolute;
        top: 10px;
        right: 10px;
      }
    }

    .attachment-audio-container {
      height: 48px;
      padding: 10px;
    }

    .attachment-play-audio-container {
      width: 100%;
      height: 100%;
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      .loading-audio {
        text-align: center;
      }

      .audio-player {
        display: block;
        cursor: auto;
        height: 2em;
      }

      .audio-with-download-button {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
      }
    }
  }
`;

const StyledReplyApp = styled.div`
  position: relative;
  background-color: #fff;

  #customer-inbox-reply-window-header {
    display: flex;
    margin: 8px;
    align-items: center;

    #customer-inbox-reply-window-close-icon-wrapper {
      cursor: pointer;
      margin-left: auto;
    }

    .spacer {
      border-right: 1px solid #d8d8d8;
      width: 0;
      height: 12px;
      margin: 0 8px;
    }

    h4 {
      margin: 0;
    }
  }
`;

const StyledFloatingButton = styled.div`
  position: absolute;
  bottom: 16px;
  right: 24px;
`;

const StyledGenerativeAIWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10100; /* Higher than Kendo UI popups (usually 10000) */
`;

const StyledContainerAlert = styled.div`
  padding: 10px 15px;
`;

const supportedReplyTypes = [
  ConversationTypeEnum.EMAIL,
  ConversationTypeEnum.PHONE,
  ConversationTypeEnum.TEXT
];

export function ConversationPane() {
  const {
    activeListTab,
    autoLeadId,
    contactDetailsError,
    contactDetailsErrorDetails,
    customerId,
    customerName,
    customerSystem,
    defaultMessageContent,
    defaultTab,
    env,
    error,
    hasActiveTask,
    isGenerativeAIEnabled,
    getUserName,
    templateId,
    vinDealerId
  } = useContext(ConversationContext);

  // State management hooks - encapsulate all context state
  const conversationDetailsValue = useConversationDetailsState();
  const emailReplyValue = useEmailReplyState();
  const textReplyValue = useTextReplyState();
  const phoneReplyValue = usePhoneReplyState();

  // Destructure commonly used values for convenience
  const { currentConversationType, selectedConversationItem } =
    conversationDetailsValue;
  const {
    replyText: emailReplyText,
    setReplyText: setEmailReplyText,
    setReplyTextSubject: setEmailReplyTextSubject
  } = emailReplyValue;
  const { replyText: textReplyText, setReplyText: setTextReplyText } =
    textReplyValue;
  const { replyText: phoneReplyText, setReplyText: setPhoneReplyText } =
    phoneReplyValue;

  // Local state (not in contexts)
  const [canLoadList, setCanLoadList] = useState(false);
  const [showReply, setShowReply] = useState(() => {
    return !!defaultMessageContent || hasActiveTask || !!templateId;
  });
  const [stationeryOptions, setStationeryOptions] = useState<StationeryOptions>(
    { stationeries: [] }
  );

  // Track customerId to detect actual changes (not initial mount)
  const customerIdRef = useRef(customerId);

  const { "nx.cxm.show-stationery": showStationery } = useCxmFlags();

  useEffect(() => {
    if (defaultMessageContent) {
      /* eslint-disable @cspell/spellchecker */
      // NADA - ManageView sending as Text, set both email and text until they fix it
      setPhoneReplyText(defaultMessageContent);
      setTextReplyText(defaultMessageContent);
      setEmailReplyText(defaultMessageContent);
    }
  }, [defaultMessageContent]);

  // Clear all reply fields when customerId changes (switching customers)
  useEffect(() => {
    // Only clear if customerId actually changed (not initial mount)
    if (customerIdRef.current !== customerId) {
      // Clear email fields
      emailReplyValue.setReplyText("");
      emailReplyValue.setReplyTextSubject("");
      emailReplyValue.setAttachments([]);
      emailReplyValue.setSelectedToChannelValue("");
      emailReplyValue.setSelectedStationeryHTML(null);
      emailReplyValue.setTemplateHead(null);

      // Clear text fields
      textReplyValue.setReplyText("");
      textReplyValue.setAttachments([]);
      textReplyValue.setSelectedToChannelValue("");
      textReplyValue.setSelectedDealerPhoneInformation(prev => ({
        ...prev,
        phoneNumber: ""
      }));

      // Clear media state across channels
      emailReplyValue.setSelectedMedia([]);
      emailReplyValue.setEndlinkMediaMap({});
      textReplyValue.setSelectedMedia([]);
      textReplyValue.setPendingEndlinks([]);

      // Clear phone fields
      phoneReplyValue.setReplyText("");
      phoneReplyValue.setMessageCallContacted("");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      phoneReplyValue.setMessageCallType("" as any);
      phoneReplyValue.setSelectedToChannelValue("");

      // Update the ref for next comparison
      customerIdRef.current = customerId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const replyText = useMemo(() => {
    if (currentConversationType === ConversationTypeEnum.EMAIL) {
      return emailReplyText;
    } else if (currentConversationType === ConversationTypeEnum.TEXT) {
      return textReplyText;
    } else if (currentConversationType === ConversationTypeEnum.PHONE) {
      return phoneReplyText;
    }
    return "";
  }, [currentConversationType, emailReplyText, textReplyText, phoneReplyText]);

  const setReplyText = useCallback(
    (value: string | ((prev: string) => string)) => {
      if (currentConversationType === ConversationTypeEnum.EMAIL) {
        setEmailReplyText(value);
      } else if (currentConversationType === ConversationTypeEnum.TEXT) {
        setTextReplyText(value);
      } else if (currentConversationType === ConversationTypeEnum.PHONE) {
        setPhoneReplyText(value);
      }
    },
    [currentConversationType]
  );

  const showTaskInProgressInfo =
    hasActiveTask && defaultTab === currentConversationType;

  const getTaskInProgressMessage = () => {
    if (!defaultTab) return "";
    const tabName = defaultTab.toLowerCase();
    const capitalizedTabName =
      tabName.charAt(0).toUpperCase() + tabName.slice(1);
    return `${capitalizedTabName} task in progress`;
  };

  // Fetch stationery options on component mount
  useEffect(() => {
    const loadStationeryOptions = async () => {
      try {
        const getStationeryRequest = { dealerId: vinDealerId };
        const response = await getStationery(getStationeryRequest, env);
        setStationeryOptions(response);
      } catch (error) {
        setStationeryOptions({ stationeries: [] });
      }
    };
    if (vinDealerId && showStationery) {
      loadStationeryOptions();
    }
  }, [vinDealerId, env, showStationery]);

  useEffect(() => {
    setCanLoadList(customerName?.trim().length > 0 || !!contactDetailsError);
  }, [customerName, contactDetailsError]);

  // Override toggleSetShowReply with local implementation
  const toggleSetShowReply = useCallback((show?: boolean) => {
    if (typeof show === "undefined") {
      setShowReply(b => !b);
    } else {
      setShowReply(show);
    }
  }, []);

  // Merge toggleSetShowReply into conversationDetailsValue
  const conversationDetailsWithToggle = useMemo(
    () => ({
      ...conversationDetailsValue,
      toggleSetShowReply
    }),
    [conversationDetailsValue, toggleSetShowReply]
  );

  const onGenerateReplyClick = () => {
    conversationDetailsValue.setIsSuggestEdit(false);
    conversationDetailsValue.setShowGenerativeAI(true);
  };

  const onSuggestEditClick = () => {
    conversationDetailsValue.setIsSuggestEdit(true);
    conversationDetailsValue.setShowGenerativeAI(true);
  };

  const closeGenerativeAI = () => {
    conversationDetailsValue.setShowGenerativeAI(false);
  };

  const userName = useMemo(() => getUserName(), [getUserName]);

  const insertGeneratedResponse = (
    message: string,
    subject?: string,
    shouldOverwriteSubject?: boolean
  ) => {
    setReplyText(message);
    if (subject && shouldOverwriteSubject) {
      setEmailReplyTextSubject(subject);
    }
    conversationDetailsValue.setShowGenerativeAI(false);
  };

  if (error) {
    return <ErrorAlert message={error} testId={conversationListError} />;
  }

  return (
    <ConversationPaneProviders
      conversationDetailsValue={conversationDetailsWithToggle}
      emailReplyValue={emailReplyValue}
      phoneReplyValue={phoneReplyValue}
      textReplyValue={textReplyValue}
    >
      {(canLoadList && (
        <StyledConversationPane
          data-autoleadid={autoLeadId}
          data-customerid={customerId}
          data-testid={conversationListTestId}
        >
          <ConversationPaneHeader />
          {!selectedConversationItem && <ConversationPaneTabs />}
          {contactDetailsError && (
            <ErrorAlert
              details={contactDetailsErrorDetails}
              message={contactDetailsError}
              testId={contactDetailsErrorTestId}
            />
          )}
          {showTaskInProgressInfo && (
            <StyledContainerAlert>
              <Alert
                data-testid="task-in-progress-info-message-conversation-pane"
                displayCloseButton={false}
                role="info"
                type="info"
              >
                {getTaskInProgressMessage()}
              </Alert>
            </StyledContainerAlert>
          )}
          {!selectedConversationItem && (
            <ConversationPaneContent
              isFullScreenReplyWindow={
                currentConversationType === ConversationTypeEnum.EMAIL ||
                currentConversationType === ConversationTypeEnum.PHONE
              }
              showReply={showReply}
            />
          )}
          {selectedConversationItem && <MessageItemDetails />}
          {!contactDetailsError && showReply ? (
            <StyledReplyApp>
              <div id="customer-inbox-reply-window-header">
                <h4>
                  {deriveDisplayTextForReplyHeader(
                    currentConversationType || ConversationTypeEnum.NONE
                  )}
                </h4>
                {isGenerativeAIEnabled &&
                  (currentConversationType === ConversationTypeEnum.EMAIL ||
                    currentConversationType === ConversationTypeEnum.TEXT) && (
                    <>
                      <span className="spacer" />
                      <Button
                        buttonStyle="aux"
                        data-testid={`generative-ai-generate-reply-button-${activeListTab}`}
                        id={`generative-ai-generate-reply-button-${activeListTab}`}
                        onClick={onGenerateReplyClick}
                      >
                        Generate Reply
                      </Button>
                      {!isTextVisuallyEmpty(replyText) && (
                        <>
                          <span className="spacer" />
                          <Button
                            buttonStyle="aux"
                            data-testid={`generative-ai-suggest-edit-button-${activeListTab}`}
                            id={`generative-ai-suggest-edit-button-${activeListTab}`}
                            onClick={onSuggestEditClick}
                          >
                            Suggest Edit
                          </Button>
                        </>
                      )}
                    </>
                  )}
                <span
                  data-testid="customer-inbox-reply-window-close-icon-wrapper"
                  id="customer-inbox-reply-window-close-icon-wrapper"
                  onClick={() => toggleSetShowReply()}
                >
                  <XMarkIcon />
                </span>
              </div>
              <ReplyApp stationeryOptions={stationeryOptions} />
            </StyledReplyApp>
          ) : (
            !contactDetailsError &&
            currentConversationType &&
            supportedReplyTypes.includes(currentConversationType) && (
              <StyledFloatingButton>
                <OpenReplyButton
                  conversationType={
                    currentConversationType || ConversationTypeEnum.NONE
                  }
                  onOpenReplyButtonClick={toggleSetShowReply}
                />
              </StyledFloatingButton>
            )
          )}
          {isGenerativeAIEnabled &&
            conversationDetailsValue.showGenerativeAI &&
            (currentConversationType === ConversationTypeEnum.EMAIL ||
              currentConversationType === ConversationTypeEnum.TEXT) && (
              <StyledGenerativeAIWrapper>
                <GenerativeAI
                  activeListTab={activeListTab}
                  channelType={currentConversationType}
                  closeGenerativeAI={closeGenerativeAI}
                  customerId={customerId}
                  customerSystem={customerSystem}
                  env={env}
                  initialPrompt={
                    conversationDetailsValue.isSuggestEdit ? replyText : ""
                  }
                  insertGeneratedResponse={insertGeneratedResponse}
                  isSuggestEdit={conversationDetailsValue.isSuggestEdit}
                  userDisplayName={userName}
                />
              </StyledGenerativeAIWrapper>
            )}
        </StyledConversationPane>
      )) || <CenteredSpinner id="conversation-pane-loading-indicator" />}
    </ConversationPaneProviders>
  );
}

export default ConversationPane;
